/**
 * @file api-site-access.ts
 * @description Клиент `admin/site-access/*` — «Сайт закрыт на работы»
 * (phase19.16, SB138; db-design-site-closed.md §3): состояние паузы,
 * история, закрыть / продлить / открыть, пропуск сотрудника на закрытый сайт.
 *
 * DTO — из `@chaiberi/shared` (`site-closure.ts`), общие с API и витриной.
 * `apiFetch` разворачивает `{ data }`; ошибка приходит `ApiError` с `code` и
 * `details` — текст для человека подбирает `site-access/errors.ts`. Ответы
 * мутаций содержат новое состояние — его кладёт в хранилище вызывающий
 * (`useSiteAccess().apply`), отдельный перезапрос не нужен.
 */

import type {
  SiteAccessStateDto,
  SiteClosureCloseRequest,
  SiteClosureHistoryPageDto,
} from "@chaiberi/shared";

import { apiFetch } from "./api-client";
import type { SiteTextWarning } from "./api-site-pages";

const BASE = "/admin/site-access";

/** Ответ закрытия: состояние и замечания к тексту (лечебные слова, превосходная степень — не блокируют). */
export interface SiteClosureCloseResult {
  state: SiteAccessStateDto;
  warnings: SiteTextWarning[];
}

/** Пропуск сотрудника на закрытый сайт: `url` — `null`, если адрес витрины не настроен. */
export interface SiteStaffPassDto {
  token: string;
  expiresAt: string;
  url: string | null;
}

/** Чего не хватает для первого открытия (PL9): жёсткие условия. */
export type SiteLaunchMissing = "contract" | "legal_profile" | "privacy_policy";

/** Предупреждение перед первым открытием (снимается: публиковать можно). */
export interface SiteLaunchWarning {
  code: string;
  /** Страница, к которой относится (например, остался текст шаблона). */
  pageSlug?: string;
  /** Название страницы — показываем вместо slug (QA D-D8). */
  pageTitle?: string;
  /** Строки-примеры: «…», «…». */
  lines?: string[];
}

/** `GET launch-check` и `details` отказа 422 `site_launch_blocked`. */
export interface SiteLaunchCheck {
  blocked: boolean;
  missing: SiteLaunchMissing[];
  warnings: SiteLaunchWarning[];
}

/** Размер страницы истории (как в контракте §3). */
export const SITE_CLOSURE_HISTORY_TAKE = 20;

export const siteAccess = {
  /** Открыт ли сайт: паузы магазина и платформы, право управлять, время сервера. */
  state: (): Promise<SiteAccessStateDto> => apiFetch<SiteAccessStateDto>(BASE),

  /** История пауз, новые сверху; `cursor` — `nextCursor` прошлой страницы. */
  history: (cursor: string | null = null): Promise<SiteClosureHistoryPageDto> => {
    const q = new URLSearchParams({ take: String(SITE_CLOSURE_HISTORY_TAKE) });
    if (cursor) q.set("cursor", cursor);
    return apiFetch<SiteClosureHistoryPageDto>(`${BASE}/history?${q.toString()}`);
  },

  /** Закрыть сайт для покупателей (срок считает сервер по варианту `autoOpen`). */
  close: (body: SiteClosureCloseRequest): Promise<SiteClosureCloseResult> =>
    apiFetch<SiteClosureCloseResult>(`${BASE}/close`, { method: "POST", json: body }),

  /** Продлить паузу со сроком на 1 ч. */
  extend: (closureId: string): Promise<SiteAccessStateDto> =>
    apiFetch<SiteAccessStateDto>(`${BASE}/extend`, { method: "POST", json: { closureId } }),

  /** Открыть сайт; повтор по уже открытой паузе — тот же ответ. */
  open: (closureId: string): Promise<SiteAccessStateDto> =>
    apiFetch<SiteAccessStateDto>(`${BASE}/open`, { method: "POST", json: { closureId } }),

  /**
   * phase19.28 (PL9): можно ли снять паузу «запуск» — жёсткие условия и
   * предупреждения. Адрес — под тем же `admin/site-access` (контроллер API);
   * в задании оркестратора — `/admin/shop/site-access/launch-check`.
   */
  launchCheck: (): Promise<SiteLaunchCheck> => apiFetch<SiteLaunchCheck>(`${BASE}/launch-check`),

  /** Пропуск «Смотреть сайт» на 8 ч, привязанный к текущей паузе. */
  staffPass: (path?: string): Promise<SiteStaffPassDto> =>
    apiFetch<SiteStaffPassDto>(`${BASE}/staff-pass`, { method: "POST", json: path ? { path } : {} }),
};
